import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import get from 'lodash/get';

import { WorkspaceMember } from '@futrli-sdk/people';
import { ReportScheduleItem } from '@futrli-sdk/report_scheduling';

import { useOrganistionName } from '../../../../hooks/useOrganisationName';
import { AuthStoreState } from '../../../../core/types';
import { useRouteIds } from '../../../../hooks/useRouteIds';

interface UseScheduleEmailStateParams {
  reportName: string;
  initialSchedule?: ReportScheduleItem;
  members: WorkspaceMember[];
}

/**
 * Manages email-related state for the schedule report form.
 * Handles recipients, subject, body, and the review-before-sending toggle.
 * Sets sensible defaults for new schedules and populates from an existing schedule when editing.
 */
export const useScheduleEmailState = ({ reportName, initialSchedule, members }: UseScheduleEmailStateParams) => {
  const intl = useIntl();
  const { workspaceId, organisationId } = useRouteIds();
  const organisationName = useOrganistionName(workspaceId, organisationId);

  const fullName = useSelector((store: AuthStoreState) => {
    const user = store.authReducers.authenticatedUser;
    const givenName = get(user, 'attributes.given_name', '');
    const familyName = get(user, 'attributes.family_name', '');
    return !familyName ? givenName : `${givenName} ${familyName}`;
  });

  const [selectedUsers, setSelectedUsers] = useState<WorkspaceMember[]>([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [reviewBeforeSending, setReviewBeforeSending] = useState(true);

  // Default email subject
  useEffect(() => {
    if (!initialSchedule) {
      setEmailSubject(
        intl.formatMessage(
          {
            id: 'predict.default_report_email_subject',
            defaultMessage: 'You have a new report for {organisationName}'
          },
          { organisationName }
        )
      );
    }
  }, [organisationName, intl, initialSchedule]);

  // Default email body
  useEffect(() => {
    if (!initialSchedule) {
      setEmailBody(
        intl.formatMessage(
          {
            id: 'predict.default_report_email_body_v3',
            defaultMessage:
              'Hello, I’ve prepared the report {reportName} for you. Please find it attached as a PDF. All the best, {fullName}'
          },
          { reportName, fullName }
        )
      );
    }
  }, [fullName, reportName, intl, initialSchedule]);

  // Populate from initial schedule (edit mode)
  useEffect(() => {
    if (initialSchedule && members.length > 0) {
      const matchedUsers: WorkspaceMember[] = [];
      for (const user of initialSchedule.users) {
        const member = members.find(m => m.email_address === user.email_address);
        if (member) {
          matchedUsers.push(member);
        }
      }
      setSelectedUsers(matchedUsers);
      setEmailSubject(initialSchedule.email_subject);
      setEmailBody(initialSchedule.email_body);
      setReviewBeforeSending(initialSchedule.approval_email_user || false);
    }
  }, [initialSchedule, members]);

  return {
    selectedUsers,
    setSelectedUsers,
    emailSubject,
    setEmailSubject,
    emailBody,
    setEmailBody,
    reviewBeforeSending,
    setReviewBeforeSending
  };
};

export type ScheduleEmailState = ReturnType<typeof useScheduleEmailState>;
